import {computed} from 'vue'
import {defineStore} from 'pinia'
import axios from "axios";
import {GuestAppApiUrl} from "@/constants";
import {useGuestAuthStore} from "@/stores/guestAuth";
import {useBookingStore} from "@/stores/booking";
import {useGuestsStore} from "@/stores/guests";

const emptyAddress = () => ({
    type: 'private',
    companyName: '',
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    country: '',
    vatId: '',
    email: '',
})

// Lodgify liefert nur einen vollen Namen. Alles bis zum letzten Leerzeichen ist
// der Vorname — bei "Anna Maria Musterfrau" die einzige Aufteilung, die den
// Nachnamen richtig trifft.
const splitFullName = fullName => {
    const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean)

    if (parts.length === 0) {
        return {firstName: '', lastName: ''}
    }

    if (parts.length === 1) {
        return {firstName: parts[0], lastName: ''}
    }

    return {
        firstName: parts.slice(0, -1).join(' '),
        lastName: parts[parts.length - 1],
    }
}

// Ob die Rechnungs-E-Mail taugt, sagt das Backend (emailStatus,
// suggestedInvoiceEmail). Die Prüfung hier ist der Rückfall für ein Backend,
// das beides noch nicht liefert — und steht als `pattern` an den Feldern,
// damit der Browser ablehnt, was der Server ablehnen würde. type=email allein
// ist dafür zu großzügig: "max@gmailcom", "max..muster@example.de",
// ".max@example.de" oder "max@example.d3" nimmt Chrome an, der Go-Validator
// (go-playground, Tag `email`) nicht. Daher: Punkte im Namen nur einzeln und
// nicht am Rand, mindestens ein Punkt in der Domain, die Endung beginnt und
// endet mit einem Buchstaben. Bewusst ohne „-“ in Zeichenklassen: Browser
// kompilieren `pattern` mit dem v-Flag, das dort strenger ist.
const emailPattern = '[^\\s@.]+(?:\\.[^\\s@.]+)*@(?:[^\\s@.]+\\.)+[a-zA-Z\\u00A0-\\uFFEF](?:[^\\s@.]*[a-zA-Z\\u00A0-\\uFFEF])?'
const emailFormat = new RegExp(`^(?:${emailPattern})$`)

// Weiterleitungsadressen der Portale: Mails dorthin reicht das Portal an den
// Gast weiter, eine Rechnung kommt dort nicht sicher an. Dieselbe Liste führt
// das Backend.
const relayDomains = [
    'guest.booking.com',
    'guest.airbnb.com',
    'm.expediapartnercentral.com',
    'messages.homeaway.com',
]

const isRelayEmail = email => {
    const value = (email ?? '').trim().toLowerCase()
    const at = value.lastIndexOf('@')
    if (at === -1) {
        return false
    }

    const domain = value.slice(at + 1)

    return relayDomains.some(relay => domain === relay || domain.endsWith('.' + relay))
}

// "ok", "relay" oder "missing" — dieselben Werte wie emailStatus im Backend.
const emailStatusOf = email => {
    const value = (email ?? '').trim()

    if (!emailFormat.test(value)) {
        return 'missing'
    }

    return isRelayEmail(value) ? 'relay' : 'ok'
}

const isBlank = value => !(value ?? '').toString().trim()

// Was ein gespeicherter Datensatz tatsächlich enthält. Ein Teilstand aus dem
// Backoffice soll eine Vorbelegung ergänzen, nicht mit Leerstrings löschen.
const filledFields = record => Object.fromEntries(
    Object.entries(record ?? {}).filter(([, value]) => !isBlank(value))
)

// Name und Anschrift gehören jeweils zusammen: Straße aus dem einen, Ort aus
// dem anderen Datensatz ergäbe eine Adresse, die es nicht gibt.
const nameFields = ['firstName', 'lastName']
const postalFields = ['street', 'postalCode', 'city', 'country']

// model.BillingAddress.IsComplete, für ein Backend, das `complete` noch nicht
// mitschickt: Straße, PLZ, Ort und Land, dazu der Firmenname bzw. Vor- und
// Nachname — bewusst ohne E-Mail.
const isCompleteRecord = record => postalFields.every(field => !isBlank(record[field]))
    && (record.type === 'company'
        ? !isBlank(record.companyName)
        : nameFields.every(field => !isBlank(record[field])))

export const useBillingStore = defineStore('billing', () => {
    const guestAuth = useGuestAuthStore()
    const bookingStore = useBookingStore()
    const guestsStore = useGuestsStore()

    // Die Anschrift reist in der Buchungsantwort mit; ein eigener GET wäre nur
    // ein zweiter Weg zur selben Wahrheit.
    const address = computed(() => bookingStore.booking.billingAddress ?? null)

    // Erledigt ist erst eine Anschrift, die für eine Rechnung taugt. Das
    // Backoffice darf Teilstände speichern (etwa nur die E-Mail) — gälten die
    // hier schon als erledigt, unterbliebe die Übernahme aus dem Meldeschein.
    // Ein Backend ohne `complete` kennt nur "da oder nicht da".
    const hasAddress = computed(() => {
        const a = address.value
        if (!a) {
            return false
        }

        return typeof a.complete === 'boolean' ? a.complete : true
    })

    // Taugt die gespeicherte Anschrift tatsächlich für eine Rechnung? Mit
    // `complete` ist das dasselbe wie hasAddress. Ohne zählt dort auch ein
    // Teilstand aus dem Backoffice als erledigt — hier nicht. Daran hängt, ob
    // sich die E-Mail allein nachtragen lässt: der Gäste-Endpunkt nimmt nur
    // eine vollständige Anschrift an.
    const isComplete = computed(() => {
        const a = address.value
        if (!a) {
            return false
        }

        return typeof a.complete === 'boolean' ? a.complete : isCompleteRecord(a)
    })

    const emailStatus = computed(() => {
        const a = address.value
        if (!a) {
            return 'missing'
        }

        return a.emailStatus || emailStatusOf(a.email)
    })

    // Ohne E-Mail-Adresse bekommt der Gast seine Rechnung nicht — die Adresse
    // eines Portals, das nur weiterleitet, zählt dabei nicht.
    const hasEmail = computed(() => emailStatus.value === 'ok')

    // Die Weiterleitungsadresse, die erklärt, warum wir trotzdem fragen: die
    // gespeicherte, sonst die aus der Buchung. Wer über Booking.com gebucht
    // hat, hat dort ja eine E-Mail-Adresse angegeben.
    const relayEmail = computed(() => {
        if (emailStatus.value === 'relay') {
            return address.value.email
        }

        const fromBooking = (bookingStore.booking.mainGuest?.email ?? '').trim()

        return isRelayEmail(fromBooking) ? fromBooking : ''
    })

    // Die E-Mail aus der Buchung, sofern sie für die Rechnung taugt. Das
    // Backend schlägt sie vor (leer, wenn Lodgify keine oder nur eine
    // Weiterleitung liefert); ein älteres Backend kennt das Feld nicht, dann
    // prüfen wir die Lodgify-Adresse selbst.
    const suggestedEmail = computed(() => {
        const booking = bookingStore.booking

        if ('suggestedInvoiceEmail' in booking) {
            return booking.suggestedInvoiceEmail ?? ''
        }

        const email = (booking.mainGuest?.email ?? '').trim().toLowerCase()

        return emailStatusOf(email) === 'ok' ? email : ''
    })

    // Was der Hauptgast im Meldeschein schon eingetippt hat. Genau das macht
    // den privaten Fall zum Ein-Tipp-Fall: die Adresse steht bereits da.
    const mainGuest = computed(() => guestsStore.guests[0] ?? {})

    const prefill = computed(() => {
        const guest = mainGuest.value
        const fromBooking = splitFullName(bookingStore.booking.mainGuest?.fullName)

        return {
            ...emptyAddress(),
            firstName: guest.firstName || fromBooking.firstName,
            lastName: guest.lastName || fromBooking.lastName,
            street: guest.address || '',
            postalCode: guest.postalCode || '',
            city: guest.city || '',
            country: guest.country || '',
            email: suggestedEmail.value,
        }
    })

    // Reicht das, was der Meldeschein hergibt, für eine Rechnung? Nur dann
    // darf "Auf mich privat" ohne Formular durchlaufen.
    //
    // Und erst, wenn der Hauptgast gespeichert ist: das Gästeformular schreibt
    // per v-model direkt in den Store, die Felder füllen sich Zeichen für
    // Zeichen. Sonst begänne die Übernahme schon beim ersten Buchstaben im
    // Ort — und der Server lehnt einen einstelligen Ort ab.
    //
    // Gespeichert heißt: mit hash. Den vergibt der Server nur einem fertigen
    // Datensatz, nie einem Entwurf, und „Ändern“ nimmt ihn wieder weg — ein
    // Entwurf trägt also nie einen. Dasselbe Merkmal zählt guestsValidAmount
    // für den Check-in; `draft` braucht es daneben nicht.
    const canUseGuestAddress = computed(() => {
        if (!mainGuest.value.hash) {
            return false
        }

        const p = prefill.value

        return !!(p.firstName && p.lastName && p.street && p.postalCode && p.city && p.country)
    })

    // origin sagt dem Backoffice, woher die Anschrift stammt: "registration"
    // = aus dem Meldeschein übernommen, "form" = vom Gast eingetippt.
    function save(record, origin = 'form') {
        // Nur die Felder des Requests: die Antwort, aus der ein Formular
        // vorbelegt wird, trägt auch source, origin, complete & Co. Die
        // Herkunft bestimmt allein der Aufruf — ein zurückgeschicktes origin
        // "office" nähme der Server zwar an, speicherte es aber als leer.
        const payload = {origin}
        for (const field of Object.keys(emptyAddress())) {
            payload[field] = record[field] ?? ''
        }

        return axios.post(
            '/v1/booking/billing-address',
            payload,
            {
                baseURL: GuestAppApiUrl,
                headers: {
                    "X-API-Key": guestAuth.token,
                }
            }
        )
            .then(({data}) => {
                bookingStore.booking.billingAddress = data

                return data
            })
    }

    // Fehlt nur noch die E-Mail, fragt die Karte genau danach. Der Endpunkt
    // will trotzdem den ganzen Datensatz — die Anschrift muss vollständig
    // sein (isComplete) und geht unverändert mit. Kam sie aus dem Meldeschein,
    // bleibt das so; jede andere hat der Gast mit seiner E-Mail bestätigt.
    // "office" weiterzureichen brächte nichts: der Server nimmt es an,
    // speichert aber eine leere Herkunft — setzen kann es nur das Backoffice.
    //
    // Abgelehnt werden kann der Datensatz trotzdem: der Gäste-Endpunkt prüft
    // strenger (Mindestlängen), als das Backoffice speichert. Das fängt die
    // Karte ab (BillingSection, submitEmail).
    function saveEmail(email) {
        const origin = address.value?.origin === 'registration' ? 'registration' : 'form'

        return save({...address.value, email}, origin)
    }

    return {
        address,
        hasAddress,
        isComplete,
        hasEmail,
        emailStatus,
        relayEmail,
        prefill,
        canUseGuestAddress,
        emailPattern,
        emptyAddress,
        filledFields,
        isRelayEmail,
        save,
        saveEmail,
    }
})
